// 方針:
// - setDefaultConfig / setLoggerConfig で設定する。
//  - prefixFormat のプレースホルダ
//    - %loggerName: ロガー名
//    - %logLevel: ログレベル
//    - 任意のプレースホルダー（例: %appName, %app-name）を placeholders 経由で指定
//    - %%: %
// - getLogger でロガーを取得する。

type LogLevel = "trace" | "debug" | "info" | "warn" | "error" | "silent";
type PlaceholderValue = string | (() => string);
type Placeholders = Record<string, PlaceholderValue>;
type LoggerMethodName = "trace" | "debug" | "info" | "warn" | "error";
type LogMethod = (...args: unknown[]) => void;

const LEVEL_ORDER: Record<LogLevel, number> = {
  trace: 10,
  debug: 20,
  info: 30,
  warn: 40,
  error: 50,
  silent: 100,
};

const VALID_LEVELS = new Set<string>(Object.keys(LEVEL_ORDER));
const PLACEHOLDER_KEY_BODY_PATTERN = "[A-Za-z0-9_]+(?:-[A-Za-z0-9_]+)*";
const PLACEHOLDER_KEY_PATTERN = new RegExp(`^%${PLACEHOLDER_KEY_BODY_PATTERN}$`);
const PLACEHOLDER_TOKEN_PATTERN = new RegExp(`%%|%${PLACEHOLDER_KEY_BODY_PATTERN}`, "g");
const RESERVED_PLACEHOLDER_KEYS = new Set<string>(["%%", "%loggerName", "%logLevel"]);

function isPlainObject(value: unknown): value is Record<PropertyKey, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }

  const proto = Object.getPrototypeOf(value);
  // 別realmのObject.prototypeも終端はnullになるため、通常オブジェクトとして受け入れる。
  return proto === null || Object.getPrototypeOf(proto) === null;
}

function formatInvalidValue(value: unknown): string {
  if (typeof value === "object" && value !== null && !Array.isArray(value) && !isPlainObject(value)) {
    return Object.prototype.toString.call(value);
  }

  try {
    const json = JSON.stringify(value);
    if (json !== undefined) return json;
  } catch {
    // JSON化できない値は文字列表現へフォールバックする。
  }

  if (typeof value === "function") return `[function ${value.name || "anonymous"}]`;
  return String(value);
}

function validateLevel(level: unknown): void {
  if (typeof level !== "string") {
    throw new TypeError(`invalid log level: ${formatInvalidValue(level)}`);
  }

  if (!VALID_LEVELS.has(level)) {
    throw new Error(`invalid log level: ${formatInvalidValue(level)}`);
  }
}

function validatePrefixEnabled(prefixEnabled: unknown): void {
  if (typeof prefixEnabled !== "boolean") {
    throw new TypeError(`invalid prefixEnabled: ${formatInvalidValue(prefixEnabled)}`);
  }
}

function validatePrefixFormat(prefixFormat: unknown): void {
  if (typeof prefixFormat !== "string") {
    throw new TypeError(`invalid prefixFormat: ${formatInvalidValue(prefixFormat)}`);
  }
}

function validateFormatTemplate(template: unknown): void {
  if (typeof template !== "string") {
    throw new TypeError(`invalid format template: ${formatInvalidValue(template)}`);
  }
}

function validatePlaceholderKey(key: PropertyKey): void {
  if (typeof key !== "string") {
    throw new TypeError(`invalid placeholder key: ${formatInvalidValue(key)}`);
  }

  if (RESERVED_PLACEHOLDER_KEYS.has(key)) {
    throw new Error(`reserved placeholder key: ${formatInvalidValue(key)}`);
  }

  if (!PLACEHOLDER_KEY_PATTERN.test(key)) {
    throw new Error(`invalid placeholder key: ${formatInvalidValue(key)}`);
  }
}

function validatePatchPlaceholders(placeholders: unknown): void {
  if (placeholders === null) {
    return;
  }

  if (!isPlainObject(placeholders)) {
    throw new TypeError(`invalid placeholders: ${formatInvalidValue(placeholders)}`);
  }

  for (const key of Reflect.ownKeys(placeholders)) {
    if (!Object.prototype.propertyIsEnumerable.call(placeholders, key)) {
      continue;
    }

    validatePlaceholderKey(key);

    const value = placeholders[key];
    if (value !== null && typeof value !== "string" && typeof value !== "function") {
      throw new TypeError(`invalid placeholder value for ${JSON.stringify(key)}: ${formatInvalidValue(value)}`);
    }
  }
}

function validateConfigObject(partial: unknown): void {
  if (!isPlainObject(partial)) {
    throw new TypeError(`invalid config: ${formatInvalidValue(partial)}`);
  }
}

type LoggerConfig = {
  level: LogLevel;
  prefixEnabled: boolean;
  prefixFormat: string;
  placeholders: Placeholders;
};

type LoggerConfigPatch = {
  level?: LogLevel | null;
  prefixEnabled?: boolean | null;
  prefixFormat?: string | null;
  placeholders?: Record<string, PlaceholderValue | null> | null;
};

type LoggerConfigOverrides = Partial<LoggerConfig>;

// TODO: いつか消す
/** @deprecated Compatibility alias only. Prefer LoggerConfigOverrides for read-side overrides in new code. */
type PerLoggerConfig = LoggerConfigOverrides; // NOSONAR

type FormattedLogger = {
  trace: LogMethod;
  debug: LogMethod;
  info: LogMethod;
  warn: LogMethod;
  error: LogMethod;
};

type Logger = {
  readonly name: string;
  format: (template: string) => FormattedLogger;
  trace: LogMethod;
  debug: LogMethod;
  info: LogMethod;
  warn: LogMethod;
  error: LogMethod;
};

const FORMATTED_LOGGER_CACHE = Symbol("formattedLoggerCache");

type InternalLogger = Logger & {
  [FORMATTED_LOGGER_CACHE]: Map<string, FormattedLogger>;
};

type State = {
  libraryDefaults: LoggerConfig;
  defaults: LoggerConfig;
  loggerOverrides: Record<string, LoggerConfigOverrides>;
  loggers: Map<string, InternalLogger>;
};

function clonePlaceholders(placeholders: Placeholders): Placeholders {
  return { ...placeholders };
}

function cloneLoggerConfig(config: LoggerConfig): LoggerConfig {
  return {
    ...config,
    placeholders: clonePlaceholders(config.placeholders),
  };
}

function cloneLoggerConfigOverrides(config: LoggerConfigOverrides): LoggerConfigOverrides {
  const clone: LoggerConfigOverrides = { ...config };

  if (Object.hasOwn(config, "placeholders")) {
    clone.placeholders = clonePlaceholders(config.placeholders as Placeholders);
  }

  return clone;
}

function applyPlaceholdersPatch(
  base: Placeholders,
  patch: Record<string, PlaceholderValue | null>,
): Placeholders {
  const next = clonePlaceholders(base);

  for (const key of Object.keys(patch)) {
    const value = patch[key];
    if (value === null) {
      delete next[key];
      continue;
    }

    next[key] = value;
  }

  return next;
}

function noop(): void { }

function getConsoleMethod(
  method: LoggerMethodName,
): LogMethod {
  const c: Partial<Console> | undefined = globalThis.console;

  const fn = c?.[method];
  if (typeof fn === "function") return fn.bind(console);

  const fallback = c?.log;
  if (typeof fallback === "function") return fallback.bind(console);

  return noop;
}

function createLibraryDefaults(): LoggerConfig {
  return {
    level: "info",
    prefixEnabled: true,
    prefixFormat: "(%loggerName) %logLevel:",
    placeholders: {},
  };
}

function createState(): State {
  const libraryDefaults = createLibraryDefaults();
  return {
    libraryDefaults,
    defaults: cloneLoggerConfig(libraryDefaults),
    loggerOverrides: {},
    loggers: new Map(),
  };
}

const state: State = createState();

function getState(): State {
  return state;
}

function formatPrefix(
  template: string,
  loggerName: string,
  level: LogLevel,
  placeholders: Placeholders,
): string {
  const lvl = level.toUpperCase();
  return template.replaceAll(PLACEHOLDER_TOKEN_PATTERN, (token) => {
    if (token === "%%") return "%";
    if (token === "%loggerName") return loggerName;
    if (token === "%logLevel") return lvl;
    if (Object.hasOwn(placeholders, token)) {
      const value = placeholders[token];
      return typeof value === "function" ? String(value()) : String(value);
    }
    return token;
  });
}

function resolveEffectiveConfig(loggerName: string): LoggerConfig {
  const state = getState();
  const d = state.defaults;
  const loggerOverrides = state.loggerOverrides[loggerName] ?? {};

  return {
    level: loggerOverrides.level ?? d.level,
    prefixEnabled: loggerOverrides.prefixEnabled ?? d.prefixEnabled,
    prefixFormat: loggerOverrides.prefixFormat ?? d.prefixFormat,
    placeholders: { ...d.placeholders, ...(loggerOverrides.placeholders ?? {}) },
  };
}

type DeferredPrefixArg = {
  toString: () => string;
};

function createDeferredPrefixArg(
  loggerName: string,
  level: LogLevel,
  config: LoggerConfig,
): DeferredPrefixArg {
  return {
    toString: () => formatPrefix(config.prefixFormat, loggerName, level, config.placeholders),
  };
}

function createLogMethod(
  loggerName: string,
  config: LoggerConfig,
  level: LoggerMethodName,
  template?: string,
): LogMethod {
  if (LEVEL_ORDER[config.level] > LEVEL_ORDER[level]) {
    return noop;
  }

  const fn = getConsoleMethod(level);
  if (template === undefined) {
    if (!config.prefixEnabled) return fn;
    return fn.bind(null, "%s", createDeferredPrefixArg(loggerName, level, config));
  }

  if (!config.prefixEnabled) {
    return fn.bind(null, template);
  }

  return fn.bind(null, `%s ${template}`, createDeferredPrefixArg(loggerName, level, config));
}

function createNoopFormattedLogger(): FormattedLogger {
  return {
    trace: noop,
    debug: noop,
    info: noop,
    warn: noop,
    error: noop,
  };
}

function applyConfigToFormattedLogger(
  formattedLogger: FormattedLogger,
  loggerName: string,
  config: LoggerConfig,
  template: string,
): void {
  formattedLogger.trace = createLogMethod(loggerName, config, "trace", template);
  formattedLogger.debug = createLogMethod(loggerName, config, "debug", template);
  formattedLogger.info = createLogMethod(loggerName, config, "info", template);
  formattedLogger.warn = createLogMethod(loggerName, config, "warn", template);
  formattedLogger.error = createLogMethod(loggerName, config, "error", template);
}

function applyConfigToLogger(logger: InternalLogger): void {
  const name = logger.name;
  const cfg = resolveEffectiveConfig(name);
  const formattedLoggers = logger[FORMATTED_LOGGER_CACHE];

  logger.format = (template: string) => {
    validateFormatTemplate(template);

    const cached = formattedLoggers.get(template);
    if (cached) return cached;

    const currentConfig = resolveEffectiveConfig(name);
    const formattedLogger = createNoopFormattedLogger();
    formattedLoggers.set(template, formattedLogger);
    applyConfigToFormattedLogger(formattedLogger, name, currentConfig, template);
    return formattedLogger;
  };
  logger.trace = createLogMethod(name, cfg, "trace");
  logger.debug = createLogMethod(name, cfg, "debug");
  logger.info = createLogMethod(name, cfg, "info");
  logger.warn = createLogMethod(name, cfg, "warn");
  logger.error = createLogMethod(name, cfg, "error");

  for (const [template, formattedLogger] of formattedLoggers) {
    applyConfigToFormattedLogger(formattedLogger, name, cfg, template);
  }
}

function reapplyAllLoggers(): void {
  const state = getState();
  for (const logger of state.loggers.values()) {
    applyConfigToLogger(logger);
  }
}

function validateConfigPatch(patch: LoggerConfigPatch): void {
  validateConfigObject(patch);

  if (Object.hasOwn(patch, "level")) {
    if (patch.level !== null) {
      validateLevel(patch.level);
    }
  }
  if (Object.hasOwn(patch, "prefixEnabled")) {
    if (patch.prefixEnabled !== null) {
      validatePrefixEnabled(patch.prefixEnabled);
    }
  }
  if (Object.hasOwn(patch, "prefixFormat")) {
    if (patch.prefixFormat !== null) {
      validatePrefixFormat(patch.prefixFormat);
    }
  }
  if (Object.hasOwn(patch, "placeholders")) {
    validatePatchPlaceholders(patch.placeholders);
  }
}

/**
 * デフォルト設定関数
 */
function setDefaultConfig(patch: LoggerConfigPatch): void {
  const state = getState();
  validateConfigPatch(patch);

  if (Object.hasOwn(patch, "level")) {
    state.defaults.level = patch.level ?? state.libraryDefaults.level;
  }

  if (Object.hasOwn(patch, "prefixEnabled")) {
    state.defaults.prefixEnabled = patch.prefixEnabled ?? state.libraryDefaults.prefixEnabled;
  }

  if (Object.hasOwn(patch, "prefixFormat")) {
    state.defaults.prefixFormat = patch.prefixFormat ?? state.libraryDefaults.prefixFormat;
  }

  if (Object.hasOwn(patch, "placeholders")) {
    state.defaults.placeholders = patch.placeholders == null
      ? clonePlaceholders(state.libraryDefaults.placeholders)
      : applyPlaceholdersPatch(state.defaults.placeholders, patch.placeholders);
  }

  reapplyAllLoggers();
}

/**
 * ロガー設定関数
 */
function setLoggerConfig(name: string, patch: LoggerConfigPatch): void {
  const state = getState();
  const key = name?.trim();
  if (!key) {
    throw new Error("logger name must be a non-empty string");
  }

  validateConfigPatch(patch);

  const current = state.loggerOverrides[key] ?? {};
  const next = cloneLoggerConfigOverrides(current);

  if (Object.hasOwn(patch, "level")) {
    if (patch.level == null) {
      delete next.level;
    } else {
      next.level = patch.level;
    }
  }

  if (Object.hasOwn(patch, "prefixEnabled")) {
    if (patch.prefixEnabled == null) {
      delete next.prefixEnabled;
    } else {
      next.prefixEnabled = patch.prefixEnabled;
    }
  }

  if (Object.hasOwn(patch, "prefixFormat")) {
    if (patch.prefixFormat == null) {
      delete next.prefixFormat;
    } else {
      next.prefixFormat = patch.prefixFormat;
    }
  }

  if (Object.hasOwn(patch, "placeholders")) {
    if (patch.placeholders == null) {
      delete next.placeholders;
    } else {
      const nextPlaceholders = applyPlaceholdersPatch(next.placeholders ?? {}, patch.placeholders);
      if (Object.keys(nextPlaceholders).length === 0) {
        delete next.placeholders;
      } else {
        next.placeholders = nextPlaceholders;
      }
    }
  }

  if (Object.keys(next).length === 0) {
    delete state.loggerOverrides[key];
  } else {
    state.loggerOverrides[key] = next;
  }

  const logger = state.loggers.get(key);
  if (logger) applyConfigToLogger(logger);
}

/**
 * ユーティリティ関数
 */
function setLogLevel(level: LogLevel): void {
  setDefaultConfig({ level });
}
function setLoggerLevel(name: string, level: LogLevel): void {
  setLoggerConfig(name, { level });
}

/**
 * ロガー取得関数
 */
function getLogger(name: string): Logger {
  const state = getState();
  const key = name?.trim();
  if (!key) {
    throw new Error("logger name must be a non-empty string");
  }

  const cached = state.loggers.get(key);
  if (cached) return cached;

  const logger: InternalLogger = {
    name: key,
    [FORMATTED_LOGGER_CACHE]: new Map(),
    format: createNoopFormattedLogger,
    trace: noop,
    debug: noop,
    info: noop,
    warn: noop,
    error: noop,
  };

  state.loggers.set(key, logger);
  applyConfigToLogger(logger);
  return logger;
}

/**
 * 参照用関数
 */
function getDefaultConfig(): Readonly<LoggerConfig> {
  return cloneLoggerConfig(getState().defaults);
}
function getLoggerOverrides(name: string): Readonly<LoggerConfigOverrides> {
  const state = getState();
  const key = name?.trim();
  if (!key) {
    throw new Error("logger name must be a non-empty string");
  }
  return cloneLoggerConfigOverrides(state.loggerOverrides[key] ?? {});
}
function getEffectiveLoggerConfig(name: string): Readonly<LoggerConfig> {
  const key = name?.trim();
  if (!key) {
    throw new Error("logger name must be a non-empty string");
  }
  return resolveEffectiveConfig(key);
}
function getLibraryDefaults(): Readonly<LoggerConfig> {
  return cloneLoggerConfig(getState().libraryDefaults);
}

/**
 * エクスポート
 */
export {
  getDefaultConfig,
  getEffectiveLoggerConfig,
  getLibraryDefaults,
  getLogger,
  getLoggerOverrides,
  setDefaultConfig,
  setLogLevel,
  setLoggerConfig,
  setLoggerLevel,
};

export type {
  FormattedLogger,
  LogLevel,
  LoggerConfig,
  LoggerConfigOverrides,
  LoggerConfigPatch,
  Logger,
  PerLoggerConfig,
  PlaceholderValue,
  Placeholders,
};
